// src/pages/student/Products.tsx
import React, { useEffect, useState } from "react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import EnrollmentStatus from "src/components/student/billing/EnrollmentStatus";
import { ChevronDown, ChevronUp } from "lucide-react";

// FAQ data for rfLearn products
const faqData = [
  {
    id: 1,
    question: "How are rfLearn courses different from other platforms?",
    answer: "At rfLearn, we prioritize reinforcement-based learning combined with AI and human expertise. Our approach focuses on personalized learning paths, interactive sessions with qualified tutors, and adaptive content that adjusts to your learning pace. We believe in quality over quantity, providing focused, engaging content that helps you master concepts faster and retain information longer.",
    isOpen: false
  },
  {
    id: 2,
    question: "Are rfLearn subscriptions free?",
    answer: "We do not have free subscription options. However, we do provide a free demo session to everyone in order to get started with their learning journey. Premium subscriptions offer options to unlock advanced courses, unlimited tutor access, personalized learning analytics, and priority support to accelerate your learning journey.",
    isOpen: false
  },
  {
    id: 3,
    question: "Why should I choose rfLearn for my education?",
    answer: "rfLearn combines the best of AI-powered learning with human expertise. Our platform offers personalized learning experiences, one-on-one tutoring sessions, adaptive assessments, and real-time progress tracking. We focus on practical skills and knowledge application, ensuring you're not just learning but truly understanding and applying concepts.",
    isOpen: false
  },
  {
    id: 4,
    question: "Are there prerequisites for taking courses on rfLearn?",
    answer: "Most of our courses are designed to be accessible to learners at different levels. While some advanced courses may have prerequisites, we clearly indicate these requirements on each course page. For beginners, we recommend starting with our foundational courses that build essential skills progressively.",
    isOpen: false
  },
  {
    id: 5,
    question: "Will I get a certificate upon completion?",
    answer: "As of now, we do not provide any certificates",
    isOpen: false
  },
  {
    id: 6,
    question: "Can I access courses offline?",
    answer: "Selected course materials can be downloaded for offline access with premium subscriptions. However, interactive sessions with tutors and real-time assessments require an internet connection to provide the best learning experience.",
    isOpen: false
  },
  {
    id: 7,
    question: "What payment methods do you accept?",
    answer: "We accept major credit cards, and RazorPay transfers. All payments are processed securely, and you can manage your subscription and payment methods from your account settings.",
    isOpen: false
  }
];

const Products: React.FC = () => {
  // Page header store
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // FAQ state management
  const [faqItems, setFaqItems] = useState(faqData);

  // Toggle FAQ item
  const toggleFaqItem = (id: number) => {
    setFaqItems(items =>
      items.map(item =>
        item.id === id ? { ...item, isOpen: !item.isOpen } : item
      )
    );
  };

  // Set up page header
  useEffect(() => {
    setTitle("Products");
    setDescription("Explore our learning products and subscriptions");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  return (
    <>
      <div className="space-y-16">
        <EnrollmentStatus />

        {/* FAQ Section */}
        <div className="max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">FAQs</h2>
          <div className="space-y-4">
            {faqItems.map((faq) => (
              <div
                key={faq.id}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white"
              >
                <button
                  onClick={() => toggleFaqItem(faq.id)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    <div className="flex-shrink-0">
                      {faq.isOpen ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </button>
                {faq.isOpen && (
                  <div className="px-6 pb-4 bg-white">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Products;
